import { useSelector, useDispatch } from 'react-redux'
import { useEffect } from 'react'
import { mapTypes, chartTypes, QueryParams } from '../common/utils/enum'
import { setBoundaryType, setSelectedAverages } from './indicatorProfileSlice'
import {
  getBoundariesAction,
  getCentroidsAction,
} from './charts/common/mapUtils'
import useQueryParams from '../common/hooks/useQueryParams'

const BoundaryTypeSelector = ({ chartType }) => {
  const dispatch = useDispatch()
  const { setQueryParam } = useQueryParams()
  const {
    boundaryType,
    availableBoundaryTypes,
    disaggregationCountries,
    countryHierarchy,
  } = useSelector((state) => state.indicatorProfile)
  const { endpointList, grouping } = useSelector((state) => state.common)

  useEffect(() => {
    if (
      chartType === chartTypes.Map &&
      boundaryType &&
      countryHierarchy &&
      disaggregationCountries?.length > 0
    ) {
      if (boundaryType === mapTypes.Region) {
        const regionGroups = countryHierarchy?.REGION || []
        const regionCodes = grouping?.['HCL_WB_REGIONS']
          ? grouping['HCL_WB_REGIONS'].map((region) => region.id)
          : []
        const availableRegions = regionGroups
          .filter(
            (region) =>
              disaggregationCountries.some(
                (country) => country.code === region.id
              ) && regionCodes.includes(region.id)
          )
          .map((region) => region.id)
        if (availableRegions.length > 0) {
          dispatch(setSelectedAverages(availableRegions))
        }
      } else if (boundaryType === mapTypes.Continent) {
        const continentGroups = countryHierarchy?.CONTINENT || []
        const availableContinents = continentGroups
          .filter((continent) =>
            disaggregationCountries.some(
              (country) => country.code === continent.id
            )
          )
          .map((continent) => continent.id)
        if (availableContinents.length > 0) {
          dispatch(setSelectedAverages(availableContinents))
        }
      } else if (boundaryType === mapTypes.IncomeGroup) {
        const incomeGroups = countryHierarchy?.INCOME || []
        const availableIncomeGroups = incomeGroups
          .filter((income) =>
            disaggregationCountries.some(
              (country) => country.code === income.id
            )
          )
          .map((income) => income.id)
        if (availableIncomeGroups.length > 0) {
          dispatch(setSelectedAverages(availableIncomeGroups))
        }
      }
    }
  }, [
    chartType,
    boundaryType,
    countryHierarchy,
    disaggregationCountries,
    dispatch,
  ])

  const handleBoundaryTypeChange = (event) => {
    const newType = event.target.value
    dispatch(setBoundaryType(newType))
    setQueryParam(QueryParams.MapType, newType)

    if (newType === mapTypes.Region) {
      const regionGroups = countryHierarchy?.REGION || []
      const regionCodes = grouping?.['HCL_WB_REGIONS']
        ? grouping['HCL_WB_REGIONS'].map((region) => region.id)
        : []
      const availableRegions = regionGroups
        .filter(
          (region) =>
            disaggregationCountries.some(
              (country) => country.code === region.id
            ) && regionCodes.includes(region.id)
        )
        .map((region) => region.id)
      if (availableRegions.length > 0) {
        dispatch(setSelectedAverages(availableRegions))
      }
    } else if (newType === mapTypes.Continent) {
      const continentGroups = countryHierarchy?.CONTINENT || []
      const availableContinents = continentGroups
        .filter((continent) =>
          disaggregationCountries.some(
            (country) => country.code === continent.id
          )
        )
        .map((continent) => continent.id)
      if (availableContinents.length > 0) {
        dispatch(setSelectedAverages(availableContinents))
      }
    } else if (newType === mapTypes.IncomeGroup) {
      const incomeGroups = countryHierarchy?.INCOME || []
      const availableIncomeGroups = incomeGroups
        .filter((income) =>
          disaggregationCountries.some((country) => country.code === income.id)
        )
        .map((income) => income.id)
      if (availableIncomeGroups.length > 0) {
        dispatch(setSelectedAverages(availableIncomeGroups))
      }
    } else if (newType === mapTypes.Country) {
      dispatch(setSelectedAverages([]))
    }

    const boundaries = getBoundariesAction(newType, endpointList)
    if (boundaries) {
      dispatch(boundaries)
    }

    const centroids = getCentroidsAction(endpointList)
    if (centroids) {
      dispatch(centroids)
    }
  }

  const getBoundaryTypeLabel = (type) => {
    switch (type) {
      case mapTypes.Country:
        return 'Country'
      case mapTypes.Region:
        return 'Region'
      case mapTypes.Continent:
        return 'Continent'
      case mapTypes.IncomeGroup:
        return 'Income Group'
      default:
        return type
    }
  }

  return (
    <div className="tui__label_text tui__secondary_dropdown tui__chart_dropdown tui_input_select">
      <label htmlFor="boundary-type-select">Map Type</label>
      <select
        id="boundary-type-select"
        htmlFor="boundary-type-select"
        value={boundaryType || ''}
        onChange={handleBoundaryTypeChange}
      >
        {availableBoundaryTypes.map((type) => (
          <option key={type} value={type}>
            {getBoundaryTypeLabel(type)}
          </option>
        ))}
      </select>
      <span className="select-arrow"></span>
    </div>
  )
}

export default BoundaryTypeSelector
